// The sky as drawn, in a cube: the skybox and the star layers rendered from
// the Sun's place, for shaders that bend the background (the black holes'
// lensing in src/blackhole.js and src/gargantua.js). The sky map holds no
// bright stars (they are points, src/stars.js) and is log-encoded data, so
// those shaders read this instead: the same stars and band as the screen,
// in their real place.
import * as THREE from 'three';

// A layer of its own, so the cube's cameras see the sky and nothing else.
const SKY_CUBE_LAYER = 7;

export function createSkyCube(renderer, scene, { size = 1024 } = {}) {
  const halfFloat = !!(renderer.extensions && (renderer.extensions.has('EXT_color_buffer_half_float') || renderer.extensions.has('EXT_color_buffer_float')));
  const target = new THREE.WebGLCubeRenderTarget(size, {
    type: halfFloat ? THREE.HalfFloatType : THREE.UnsignedByteType,
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter,
    magFilter: THREE.LinearFilter,
  });
  // Anisotropic up front: the lens shaders would otherwise set it and flag
  // the render target for an upload it cannot take.
  target.texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
  const cubeCamera = new THREE.CubeCamera(1, 50_000_000, target);
  for (const cam of cubeCamera.children) cam.layers.set(SKY_CUBE_LAYER);

  // layers: the sky objects; prepare(screenToCube) sets their uniforms for
  // the render (full exposure, star sprites scaled from screen pixels to the
  // cube's) and returns a function that puts them back.
  function render(layers, prepare) {
    const objects = layers.filter(Boolean);
    const had = objects.map((o) => o.layers.isEnabled(SKY_CUBE_LAYER));
    objects.forEach((o) => o.layers.enable(SKY_CUBE_LAYER));
    // One cube texel spans (pi/2) / size radians.
    const restore = prepare ? prepare(Math.PI / 2 / size) : null;
    const prevXr = renderer.xr.enabled;
    renderer.xr.enabled = false;
    cubeCamera.position.set(0, 0, 0);
    cubeCamera.updateMatrixWorld(true);
    cubeCamera.update(renderer, scene);
    renderer.xr.enabled = prevXr;
    if (restore) restore();
    objects.forEach((o, i) => { if (!had[i]) o.layers.disable(SKY_CUBE_LAYER); });
  }

  return {
    texture: target.texture,
    render,
    dispose: () => target.dispose(),
  };
}

// An empty cube for the samplers until the sky is ready.
export function placeholderSkyCube() {
  return new THREE.WebGLCubeRenderTarget(1).texture;
}
